// src/lib/logger.ts

import log, { LogLevelNames, LogLevelNumbers } from "loglevel";

const queryParams = new URLSearchParams(window.location.search);
const hasRemotejs = queryParams.has("remotejs");

// Сохраняем оригинальную фабрику методов для последующего вызова
const originalFactory = log.methodFactory;

// Определяем стилизованный префикс и его стили
const styledPrefix = "%c FXID ";
const styledPrefixCss = "background: #000; color: #fff; padding: 2px 4px; border-radius: 3px;";
// Стиль для сброса форматирования для остальной части сообщения
const resetCss = "";

// Переопределяем фабрику методов, чтобы добавить префикс и обработать remotejs
log.methodFactory = (
	methodName: LogLevelNames,
	level: LogLevelNumbers,
	loggerName: string | symbol
): log.LoggingMethod => {
	const rawMethod = originalFactory(methodName, level, loggerName);

	return (...args: unknown[]) => {
		// Проверяем, является ли первый аргумент строкой для форматирования
		const isFirstArgFormatString = typeof args[0] === "string";

		let newArgs: unknown[];

		if (isFirstArgFormatString) {
			const userFormatString = args[0] as string;
			const remainingArgs = args.slice(1);

			// --- Объяснение трюка ---
			// Чтобы стилизовать только префикс, мы делаем следующее:
			// 1. Формируем новую строку формата: `НАШ_СТИЛЬ НАШ_ТЕКСТ СБРОС_СТИЛЯ ПОЛЬЗОВАТЕЛЬСКИЙ_ТЕКСТ`
			//    Пример: `%c FXID %crouterBasePath: %s`
			// 2. Передаем аргументы для стилей: сначала для нашего префикса, потом пустой для сброса.
			//    Пример: `['стиль_для_fxid', '', '/']`
			// В итоге console.log правильно отформатирует только префикс.
			newArgs = [
				`${styledPrefix}%c${userFormatString}`, // -> "%c FXID %crouterBasePath: %s"
				styledPrefixCss,
				resetCss,
				...remainingArgs
			];
		} else {
			// Если первый аргумент не строка (например, log.debug({ object })),
			// то просто выводим стилизованный префикс, а за ним остальные аргументы.
			// В этом случае сброс не нужен, т.к. префикс и так является отдельным аргументом.
			newArgs = [styledPrefix, styledPrefixCss, ...args];
		}

		// Вызываем оригинальный метод с новыми, модифицированными аргументами
		rawMethod(...newArgs);

		// Дублируем вывод в глобальную консоль в режиме remotejs
		if (hasRemotejs) {
			// eslint-disable-next-line no-console
			const consoleMethod = console[methodName as keyof Console];
			if (typeof consoleMethod === "function") {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
				(consoleMethod as Function)(...newArgs);
			}
		}
	};
};

// Пересобираем логгер с новой фабрикой методов
log.rebuild();

// Применяем конфигурацию уровня логирования из переменных окружения
log.setDefaultLevel(
	import.meta.env.VITE_APP_ENV_OVERRIDE_NAME === "prod"
		? "warn"
		: import.meta.env.VITE_APP_ENV_OVERRIDE_NAME === "dev"
			? "trace"
			: "debug"
);

// Логируем сообщение об инициализации
log.debug(hasRemotejs ? "Remotejs enabled: FXID prefix + console duplication" : "Standard mode: FXID prefix active");

export default log;
